import { queryOptions, useQuery } from '@tanstack/react-query';

import {
  fetchUserCard,
  fetchUserChat,
  fetchUserChats,
  fetchUserContent,
  fetchUserLedger,
  searchUsers,
  type AdminChatsPage,
  type AdminChatTranscriptPage,
  type AdminContentPage,
  type AdminLedgerPage,
  type AdminUserCard,
  type AdminUsersSearchResult,
  type SearchUsersRequest,
} from './adminApi';

/**
 * react-query wrappers for admin_users_v2 (support-admin Phase 1 §1.1/§1.4,
 * ClickUp 869f0ev1r / 869f0ev2a) — same shape as useMe.ts: one hook per
 * endpoint, no client-side caching contract beyond react-query's own.
 */

export type SearchUsersParams = SearchUsersRequest & { q: string; page: number };

/** How long a search page counts as fresh — remounting the page or switching
 * tabs within this window reuses it instead of asking the database again. */
const SEARCH_STALE_MS = 30_000;

/**
 * The search query, exported so its guardrails are testable. Prod incident
 * 30.09.2026: the list and its count ran 16-19 minutes per statement and the
 * console re-issued them about once a minute, piling eight onto the
 * database. So: no retry (a 503 timeout is not transient in seconds), no
 * refetch on focus/reconnect, a stale window, and react-query's abort signal
 * handed to fetch so a superseded search is cancelled in the browser. The
 * server-side bound is the statement_timeout in apps/api-py app/db/admin.py.
 */
export function searchUsersQueryOptions({ limit = 20, ...params }: SearchUsersParams) {
  const {
    q,
    page,
    subscription,
    sort,
    dir,
    active_within_days,
    inactive_days,
    active_from,
    active_to,
    credits_min,
    credits_max,
  } = params;
  return queryOptions<AdminUsersSearchResult>({
    // Every request parameter participates in the key so a new search, set,
    // order or filter always refetches rather than reusing a stale page. The
    // activity presets reach here as day counts (not timestamps) so the key
    // stays stable across renders.
    queryKey: [
      'admin-users',
      'search',
      q,
      page,
      limit,
      subscription ?? '',
      sort ?? '',
      dir ?? '',
      active_within_days ?? '',
      inactive_days ?? '',
      active_from ?? '',
      active_to ?? '',
      credits_min ?? '',
      credits_max ?? '',
    ],
    queryFn: ({ signal }) => searchUsers({ ...params, q: q || undefined, limit }, { signal }),
    placeholderData: (previous) => previous,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: SEARCH_STALE_MS,
  });
}

export function useSearchUsers(params: SearchUsersParams) {
  return useQuery(searchUsersQueryOptions(params));
}

export function useUserCard(userId: string | undefined) {
  return useQuery<AdminUserCard | null>({
    queryKey: ['admin-users', 'card', userId],
    queryFn: () => fetchUserCard(userId as string),
    enabled: Boolean(userId),
  });
}

export function useUserContent(userId: string | undefined, cursor: string | null) {
  return useQuery<AdminContentPage>({
    queryKey: ['admin-users', 'content', userId, cursor],
    queryFn: () => fetchUserContent(userId as string, { cursor: cursor ?? undefined }),
    enabled: Boolean(userId),
    placeholderData: (previous) => previous,
  });
}

/** Every admin role — the coin ledger is history as ROWS (869f4c8v4). */
export function useUserLedger(userId: string | undefined, cursor: string | null) {
  return useQuery<AdminLedgerPage>({
    queryKey: ['admin-users', 'ledger', userId, cursor],
    queryFn: () => fetchUserLedger(userId as string, { cursor: cursor ?? undefined }),
    enabled: Boolean(userId),
    placeholderData: (previous) => previous,
  });
}

/** Superadmin only — the caller gates the tab with `canReadUserChats`; an
 * unexpected 403 surfaces as the query's error state, never as a retry loop. */
export function useUserChats(userId: string | undefined, cursor: string | null, enabled: boolean) {
  return useQuery<AdminChatsPage>({
    queryKey: ['admin-users', 'chats', userId, cursor],
    queryFn: () => fetchUserChats(userId as string, { cursor: cursor ?? undefined }),
    enabled: Boolean(userId) && enabled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
  });
}

export function useUserChat(
  userId: string | undefined,
  chatId: string | null,
  cursor: string | null,
) {
  return useQuery<AdminChatTranscriptPage>({
    queryKey: ['admin-users', 'chat', userId, chatId, cursor],
    queryFn: () =>
      fetchUserChat(userId as string, chatId as string, { cursor: cursor ?? undefined }),
    enabled: Boolean(userId) && Boolean(chatId),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
  });
}
